<template>
  <div class="alert-container" v-if="visible">
    <div class="alert" :class="['alert-' + type, 'alert-' + context]">
      <div class="alert-icon">
        <NsIcon :icon-name="icon" :color="color" style="width:30px;height:30px;" />
      </div>
      <div class="alert-head">
        <h4 class="title-small-bold"><slot name="head">{{ title }}</slot></h4>
      </div>
      <div class="alert-close-button" v-if="hasDismissListener">
        <span>
          <svg viewBox="0 0 12 12" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" @click="$emit('dismiss')">
            <g fill="none" stroke="#000000" stroke-width="1">
              <path stroke-linecap="round" d="M 0 0 L 12 12 M 0 12 L 12 0"></path>
            </g>
          </svg>
        </span>
      </div>
      <div class="alert-content">
        <div class="alert-body p-medium">
          <slot name="body"></slot>
        </div>
        <div class="alert-foot p-small" v-if="$slots.foot">
          <slot name="foot"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { StringService } from '@nuskin/ns-util'

import NsIcon from '../ns-icon-vue/ns-icon.vue'

const ALERT_CONTEXT = [
  'error',
  'info',
  'notice',
  'success',
  'warning'
]

const ALERT_TYPES = [
  'banner',
  'card'
]

export default {
  name: 'ns-alert',
  components: {
    NsIcon
  },
  props: {
    context: {
      type: String,
      required: false,
      default: 'info',
      validator (value) {
        return ALERT_CONTEXT.indexOf(value) >= 0
      }
    },
    type: {
      type: String,
      required: false,
      default: 'banner',
      validator (value) {
        return ALERT_TYPES.indexOf(value) >= 0
      }
    },
    visible: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    hasDismissListener () {
      return this.$listeners && this.$listeners.dismiss
    },
    color () {
      switch (this.context) {
        case 'error':
          return '#B2140A'
        case 'notice':
          return '#35b2f0'
        case 'success':
          return '#6BC56A'
        case 'warning':
          return '#FCBC00'
        default:
          return '#123DBA'
      }
    },
    icon () {
      switch (this.context) {
        case 'error':
          return 'icon-error-color-filled'
        case 'success':
          return 'icon-success-filled'
        case 'warning':
          return 'icon-warning-filled'
        default:
          return 'icon-info-filled'
      }
    },
    title () {
      switch (this.context) {
        case 'error':
          return this.content['error']
        case 'notice':
          return this.content['notice']
        case 'success':
          return this.content['success']
        case 'warning':
          return this.content['warning']
        default:
          return this.content['information']
      }
    }
  },
  data () {
    return {
      content: {}
    }
  },
  created () {
    StringService.getStrings([
      {
        key: 'error',
        dflt: 'Error'
      },
      {
        key: 'information',
        dflt: 'Information'
      },
      {
        key: 'notice',
        dflt: 'Notice'
      },
      {
        key: 'success',
        dflt: 'Success'
      },
      {
        key: 'warning',
        dflt: 'Warning'
      }
    ]).then((content) => {
      this.content = content
    })
  }
}

</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";

$color-ash: map.get(colors.$baseColors, ash);
$color-blackhole: map.get(colors.$baseColors, blackhole);
$color-black: #000000;

$alert-context: (
  error: (
    color: map.get(colors.$baseColors, color-error)
  ),
  info: (
    color: map.get(colors.$baseColors, information-color)
  ),
  notice: (
    color: map.get(colors.$baseColors, notice-color)
  ),
  success: (
    color: map.get(colors.$baseColors, success)
  ),
  warning: (
    color: map.get(colors.$baseColors, warning-color)
  )
);

$alert-types: (
  banner: (
    padding-horizontal: 45px,
    padding-vertical: 15px,
    bar-position: 'top',
    content-column-start: row-center,
    content-column-end: row-center,
    foot-visibility: block
  ),
  card: (
    padding-horizontal: 24px,
    padding-vertical: 24px,
    bar-position: 'left',
    content-column-start: row-left,
    content-column-end: row-right,
    foot-display: none
  )
);

* {
  box-sizing: border-box;
  webkit-font-smoothing: antialiased;
}

.alert-container {
  $gutter-width: 10px;

  $close-button-width: 12px;
  $close-button-height: 12px;
  $icon-width: 24px;
  $icon-height: 24px;

  .alert {
    position: relative;
    display: grid;
    grid-template-columns: [row-left] $icon-width [space] $gutter-width [row-center] auto [space] $gutter-width [row-right] $close-button-width;
    grid-template-rows: [row1] 32px;
    grid-auto-flow: row;
    height: auto;
    border: 1px solid $color-ash;
  }

  .alert-close-button {
    align-self: center;
    grid-column-start: row-right;
    grid-column-end: row-right;
    grid-row-start: row1;
    grid-row-end: row1;
  }

  .alert-icon {
    align-self: baseline;
    grid-column-start: row-left;
    grid-column-end: row-left;
    grid-row-start: row1;
    grid-row-end: row1;
    width: $icon-width;
    height: $icon-height;
  }

  .alert-head {
    align-self: center;
    grid-column-start: row-center;
    grid-column-end: row-center;
    grid-row-start: row1;
    grid-row-end: row1;

    h1, h2, h3, h4, h5, h6 {
      margin: 0;
    }
  }

  // .alert-banner, .alert-card, ...
  @each $type-name, $type in $alert-types {
    $padding-horizontal: map.get($type, 'padding-horizontal');
    $padding-vertical: map.get($type, 'padding-vertical');

    .alert-#{$type-name} {
      min-height: $icon-height + $padding-horizontal * 2;
      padding: $padding-vertical $padding-horizontal;

      // .alert-error, .alert-info, ...
      @each $context-name, $context in $alert-context {
        &.alert-#{$context-name} {
          border-#{map.get($type, 'bar-position')}: 8px solid map.get($context, 'color');

          .alert-head {
            color: map.get($context, 'color')
          }
        }
      }

      .alert-content {
        grid-column-start: map.get($type, 'content-column-start');
        grid-column-end: map.get($type, 'content-column-end');
      }

      .alert-foot {
        display: map.get($type, 'foot-display')
      }
    }
  }

  .alert-head {
    display: flex;
    flex-direction: column;
    justify-content: center;

    h4 {
      color: inherit;
    }
  }

  .alert-content {
    margin-top: 8px;
    color: $color-blackhole;
  }

  .alert-foot {
    margin-top: 8px;
    color: $color-blackhole;
  }
}

</style>
